import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil, Plus, Search, Trash2 } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { ApiError } from '@/core/api';
import { useMembers } from '@/features/members';
import { MemberSelect } from '@/features/members';
import { useSession } from '@/stores/session';
import { TaskFormDialog } from '../components/TaskFormDialog';
import {
  PRIORITY_LABEL,
  TASK_STATUSES,
  canManageTasks,
  type TaskStatus,
} from '../constants';
import {
  useCreateTask,
  useDeleteTask,
  useSetTaskStatus,
  useTasks,
  useUpdateTask,
} from '../queries';
import type { Task, TaskInput } from '../types';

const ALL = 'all';
const PRIORITY_VARIANT: Record<string, 'destructive' | 'secondary' | 'outline'> = {
  high: 'destructive',
  medium: 'secondary',
  low: 'outline',
};

function useAssigneeNames() {
  const { data } = useMembers();
  return useMemo(() => {
    const map = new Map<number, string>();
    for (const m of data?.results ?? []) map.set(m.user.id, m.user.full_name);
    return map;
  }, [data]);
}

export function TasksListPage() {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<TaskStatus | typeof ALL>(ALL);
  const [assignee, setAssignee] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Task | undefined>(undefined);
  const [toDelete, setToDelete] = useState<Task | null>(null);

  const navigate = useNavigate();
  const openTask = (task: Task) => navigate(`/tasks/${task.public_id}`);
  const role = useSession((s) => s.role);
  const canManage = canManageTasks(role);
  // Only the roles that see *all* tasks (TASK_MANAGE_ROLES — Founder / Super Admin
  // / HR) get the by-assignee filter; everyone else already sees only their own.
  const canFilterByAssignee = role === 'founder' || role === 'super_admin' || role === 'hr';
  // Columns: Title, Status, Priority, Assignee, Due (+ Actions for managers).
  const colSpan = canManage ? 6 : 5;

  const { data, isPending, isError, error, isFetching } = useTasks({
    search: search || undefined,
    status: statusFilter === ALL ? undefined : statusFilter,
    assignee: canFilterByAssignee && assignee ? Number(assignee) : undefined,
  });
  const assignees = useAssigneeNames();

  const create = useCreateTask();
  const update = useUpdateTask();
  const remove = useDeleteTask();
  const setStatus = useSetTaskStatus();

  const tasks = data?.results ?? [];

  function applySearch(e: React.FormEvent) {
    e.preventDefault();
    setSearch(searchInput.trim());
  }

  function openNew() {
    setEditing(undefined);
    setDialogOpen(true);
  }

  function openEdit(task: Task) {
    setEditing(task);
    setDialogOpen(true);
  }

  async function handleSubmit(input: TaskInput) {
    if (editing) {
      await update.mutateAsync({ id: editing.public_id, input });
    } else {
      await create.mutateAsync(input);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">Tasks</h1>
        {canManage ? (
          <Button onClick={openNew}>
            <Plus className="size-4" /> New task
          </Button>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <form onSubmit={applySearch} className="relative max-w-sm flex-1">
          <Search className="text-muted-foreground absolute top-2.5 left-2.5 size-4" />
          <Input
            className="pl-8"
            placeholder="Search tasks"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </form>
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as TaskStatus | typeof ALL)}>
          <SelectTrigger className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All statuses</SelectItem>
            {TASK_STATUSES.map((s) => (
              <SelectItem key={s.value} value={s.value}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {canFilterByAssignee ? (
          <MemberSelect
            value={assignee}
            onChange={setAssignee}
            clearable
            placeholder="All assignees"
            queryKey="tasks-assignee-filter"
            triggerClassName="w-56"
          />
        ) : null}
      </div>

      {isError ? (
        <p className="text-destructive text-sm">
          {(error as unknown as ApiError)?.message ?? 'Failed to load tasks.'}
        </p>
      ) : (
        <div className="rounded-md border" aria-busy={isFetching}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead className="w-40">Status</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead>Assignee</TableHead>
                <TableHead>Due</TableHead>
                {canManage ? <TableHead className="w-24 text-right">Actions</TableHead> : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isPending ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={colSpan}>
                      <Skeleton className="h-6 w-full" />
                    </TableCell>
                  </TableRow>
                ))
              ) : tasks.length ? (
                tasks.map((task) => {
                  const done = task.status === 'done';
                  return (
                    <TableRow
                      key={task.public_id}
                      className="cursor-pointer"
                      onClick={() => openTask(task)}
                    >
                      <TableCell className={done ? 'text-muted-foreground line-through' : 'font-medium'}>
                        {task.title}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Select
                          value={task.status}
                          onValueChange={(v) =>
                            setStatus.mutate({ id: task.public_id, status: v as TaskStatus })
                          }
                        >
                          <SelectTrigger className="h-8 w-36" aria-label="Task status">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {TASK_STATUSES.map((s) => (
                              <SelectItem key={s.value} value={s.value}>
                                {s.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Badge variant={PRIORITY_VARIANT[task.priority]}>
                          {PRIORITY_LABEL[task.priority]}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {task.assignee ? (assignees.get(task.assignee) ?? '…') : '—'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {task.due_date ? new Date(task.due_date).toLocaleDateString() : '—'}
                      </TableCell>
                      {canManage ? (
                        <TableCell
                          className="text-right"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button size="icon" variant="ghost" aria-label="Edit task" onClick={() => openEdit(task)}>
                            <Pencil className="size-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            aria-label="Delete task"
                            onClick={() => setToDelete(task)}
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        </TableCell>
                      ) : null}
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={colSpan} className="text-muted-foreground text-center">
                    No tasks found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <TaskFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        task={editing}
        onSubmit={handleSubmit}
      />

      <AlertDialog open={Boolean(toDelete)} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete task?</AlertDialogTitle>
            <AlertDialogDescription>
              “{toDelete?.title}” will be permanently removed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (toDelete) remove.mutate(toDelete.public_id);
                setToDelete(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
